import { equals, pathOr, cond, T, always, head, keys, all, map, any, toString, includes, __ } from "ramda";
import { isNotEmpty } from "src/utilities/hasValue";
import { getConsentRule } from "../../Intents/consent_rules";
import { doNothing } from "../Formatters/formatters";
import { getFilteredKeyFieldAnswer } from "../utilities";
import { conditionalFuncs } from "src/utilities/conditionalFn";
import { filterTheResourcesByRole } from "src/utilities/borrowers";
import { ROLE_BORROWER, ROLE_COBORROWER } from "src/constants";
import { LoanRole } from 'src/types/Loan';
import { getStoreBasedOnAuthenticationType } from "../Configs/apiResouces";

/*
   * checkDeactivatingValueWithDifferentFnsForComparisionType - method which checks whether intent answer and deactivating condition matches for number type(basically comparision)
 * @param checkFn contains relation function instance
 * @param conditionName contains relation function name which is to be used for comparing
 * @param activatingCondition -single conditions for a particular intent
 * @param userIntents single users list of intent answers
 * @param compareAnswer exact answer,if its sent then it will be used,if not answer from deactivation condition used
 * @returns boolean(true or false)
 */
const checkDeactivatingValueWithDifferentFnsForComparisionType = (
  checkFn: any,
  conditionName: string,
  activatingCondition: any,
  userIntents: any,
  compareAnswer: any
) =>
  checkFn(
    Number(
      compareAnswer ||
      getFilteredKeyFieldAnswer(
        pathOr('', [conditionName, 'key_field'], activatingCondition),
        userIntents
      )
    ),
    Number(pathOr('', [conditionName, 'target_value'], activatingCondition))
  );




//////////////////  PRIVATE FUNCTIONS ////////////////////////

/**
 * checkDeactivatingValueWithDifferentFnsForStringType - method which checks whether intent answer and deactivating condition matches for string type
 * @param checkFn contains relation function instance
 * @param conditionName contains relation function name which is to be used for comparing
 * @param activatingCondition -single condition for a particular intent
 * @param intents single user's list intent anwers
 * @param compareAnswer exact answer,if its sent then it will be used,if not answer from deactivation condition used
 * @returns boolean(true or false)
 */
const checkDeactivatingValueWithDifferentFnsForStringType = (
  checkFn: Function,
  conditionName: string,
  activatingCondition: any,
  useIntents: any,
  compareAnswer: any,
  loanUsersSpecificIntents?: any,
  loanApp?:any
) => {
  const res = checkFn(
    pathOr('', [conditionName, 'target_value'], activatingCondition),
    compareAnswer ||
    getFilteredKeyFieldAnswer(
      pathOr('', [conditionName, 'key_field'], activatingCondition),
      useIntents
    )
  );
  return res;
};


const checkDeactivatingBasedOnRulesContext = (
  conditionName: string,
  activatingCondition: any,
  ruleFlagger: any,
  userIntents: any
) => {
  const answer = getFilteredKeyFieldAnswer(
    pathOr('', [conditionName, 'key_field'], activatingCondition),
    userIntents
  );
  const targetValue = pathOr(
    '',
    [conditionName, 'target_value'],
    activatingCondition
  );

  return equals(toString(ruleFlagger(answer)), toString(targetValue));
};

const checkDeactivatingBasedOnResourceRulesContext = (
  conditionName: string,
  activatingCondition: any,
  ruleFlagger: any,
  userIntents: any,
  loanApp?: any
) => {
  const targetValue = pathOr(
    '',
    [conditionName, 'target_value'],
    activatingCondition
  );

  return equals((ruleFlagger(loanApp)), (targetValue));
};



const checkDeactivatingValueWithDifferentFnsForArray = (
  checkFn: Function,
  conditionName: string,
  activatingCondition: any,
  userIntents: any,
  compareAnswer: any
) => {
  let keyFields = pathOr([], [conditionName, 'key_field'], activatingCondition);

  keyFields = Array.isArray(keyFields) ? keyFields : [keyFields];

  const mappedData= map((keyField) => {
    const intentAnswer = getFilteredKeyFieldAnswer(keyField, userIntents);
    return checkFn(
      compareAnswer || intentAnswer,
      pathOr([], [conditionName, 'target_value'], activatingCondition)
    );
  }, keyFields);
  return any(equals(true))(mappedData);
};
 

  
//////////////////  PRIVATE FUNCTIONS ////////////////////////

/**
 * checkValueWithRelationsFn - function which checks the value of intent and
 * comparision intent with relations configured
 * @param compareAnswer exact answer as string which will used for comparision if givien
 * @param userIntents single user's intents from user reducer(i.e from API)
 * @param activatingCondition - single activation condition that used for comparision
 * @returns boolean(true or false)
 */
const checkValueWithRelationsFn = (
  compareAnswer: any,
  userIntents: any,
  activatingCondition: any,
  loanUsersSpecificIntents?: any,
  loanApp?:any
) => {

  const checkFn = (conditionName: string) => conditionalFuncs(conditionName)

  const stringValidator = (conditionName: string) =>
    checkDeactivatingValueWithDifferentFnsForStringType(
      checkFn(conditionName),
      conditionName,
      activatingCondition,
      userIntents,
      compareAnswer,
      loanUsersSpecificIntents,
      loanApp
    )

    const numberValidator = (conditionName: string) =>
      checkDeactivatingValueWithDifferentFnsForComparisionType(
        checkFn(conditionName),
        conditionName,
        activatingCondition,
        userIntents,
        compareAnswer
      )

     const arrayValidator =  (conditionName: string) =>
        checkDeactivatingValueWithDifferentFnsForArray(
          checkFn(conditionName),
          conditionName,
          activatingCondition,
          userIntents,
          compareAnswer
        )

  return cond([
    [
      includes(__, ['equals', 'not_equals', 'isEmpty', 'isNotEmpty']),
      stringValidator,
    ],
    [
      includes(__, ['lt', 'lte', 'gt', 'gte']),
      numberValidator,
    ],
    [
      includes(__, ['contains', 'includes', 'not_contains', 'not_includes', 'not_contains_with_pipe_separator']),
      arrayValidator,
    ],
    //used for deactivating intents based on rule context
    [
      equals('rule_context'),
      (conditionName: string) => {
        const apply = pathOr(
          '',
          ['rule_context', 'apply'],
          activatingCondition
        );
        const ruleFlagger: any = pathOr(
          doNothing,
          ['ruleFlagger'],
          getConsentRule(apply)
        );

        return checkDeactivatingBasedOnRulesContext(
          conditionName,
          activatingCondition,
          ruleFlagger,
          userIntents
        );
      },
    ],
    [
      equals('resource_rule_context'),
      (conditionName: string) => {
        const apply = pathOr(
          '',
          ['resource_rule_context', 'apply'],
          activatingCondition
        );
        const ruleFlagger: any = pathOr(
          doNothing,
          ['ruleFlagger'],
          getConsentRule(apply)
        );

        return checkDeactivatingBasedOnResourceRulesContext(
          conditionName,
          activatingCondition,
          ruleFlagger,
          userIntents,
          loanApp
        );
      },
    ],
    [
      includes(__, [ROLE_BORROWER, ROLE_COBORROWER]), (conditionName: string) => {
        const primaryUserIntents = pathOr(userIntents, [0, 'intents'], filterTheResourcesByRole(
          loanUsersSpecificIntents,
          conditionName as LoanRole
        ))
        const thisCondition = pathOr({}, [conditionName], activatingCondition)
        const deActivatedUpon = pathOr([], ['de_activated_upon'], thisCondition)
        if (isNotEmpty(deActivatedUpon)) {
          const thisResult = ifToAddOrRemoveThisIntentWhenAllPass(
            deActivatedUpon,
            primaryUserIntents,
            compareAnswer,
            pathOr('', ['pipe_with'], thisCondition),
            loanUsersSpecificIntents,
            loanApp
          );
          return pathOr(false, ['remove'], thisResult);
        }
        return false
      }
    ],
    [T, always(false)],
  ])(head(keys(activatingCondition)));
};

/**
 *
 * @param activationConds contains list of activation conditions for the single Intent
 * @param userIntents contains list of Intents users have answered
 * @param compareAnswer exact answer which needs to be compared
 * @param pipeWith string
 * @returns object as {
 * checkedResult : boolean (means that check has been done),
 * remove:boolean (if to remove,then return true,if not false),
 * add:boolean (if to add then returrn true,if not false)
 * }
 */
// pipeWith will decide which logical option we need to apply on multiple conditions by default it will apply Or (any) logical operation
export const ifToAddOrRemoveThisIntentWhenAllPass = (
  activationConds: any,
  userIntents: any,
  compareAnswer?: string,
  pipeWith?: string,
  loanUsersSpecificIntents?: any,
  loanApp?:any
) => {
  const results: any = map(
    (activationCond: any) => checkValueWithRelationsFn(compareAnswer, userIntents, activationCond, loanUsersSpecificIntents, loanApp),
    activationConds
  )

  const checkWith = cond([
    [equals('and'), () => all(equals(true))(results)],
    [equals('any_false'), () =>  any(equals(false))(results)],
    [T, () => any(equals(true))(results)],
  ])(pipeWith);
  return checkWith
    ? { checkedResult: true, remove: true, add: false }
    : { checkedResult: true, remove: false, add: true };
};

/**
 * checkDeactivatedUponOnEventContext - used for conditionally running the event based on deactivated upon
 * @param event contains the array of objects of events configured in single site intent
 * @param userIntents single user's list of intents from user reducer(i.e from API)
 * @returns object({shouldRun:true} or {shouldRun:false})
 */
export const checkDeactivatedUponOnEventContext = (
  event: any,
  userIntents: any
) => {
  const deactivatedUpon = pathOr({}, ['de_activated_upon'], event);
  if (isNotEmpty(deactivatedUpon)) {
    const storeClass =  getStoreBasedOnAuthenticationType()
    const stateStore = new storeClass({})
    const loanUsersSpecificIntents = pathOr([], ['loanUsersSpecificIntents'], stateStore.getStateFromStore());
    const loanApp = pathOr({}, ['loanapps'], stateStore.getStateFromStore());
    const checkWith = ifToAddOrRemoveThisIntentWhenAllPass(
      deactivatedUpon,
      userIntents,
      '',
      '',
      loanUsersSpecificIntents,
      loanApp
    );
    return {
      shouldRun: !pathOr(false, ['remove'], checkWith),
    };
  }
  return { shouldRun: true }
};

/**
* checkDeactivatedUponOnStatusContext - used for conditionally running the event based on deactivated upon
* @param status contains the array of status configured in single site intent and de_activated_upon
* @param userIntents single user's intents from user reducer(i.e from API)
* @returns object({shouldRun:true} or {shouldRun:false})
*/
export const checkDeactivatedUponOnStatusContext = (
  status: any,
  userIntents: any
) => {
  const deactivatedUpon = pathOr({}, ['de_activated_upon'], status);
  if (isNotEmpty(deactivatedUpon)) {
      const storeClass =  getStoreBasedOnAuthenticationType()
      const stateStore = new storeClass({})
      const loanUsersSpecificIntents = pathOr([], ['loanUsersSpecificIntents'], stateStore.getStateFromStore());
      const loanApp = pathOr({}, ['loanapps'], stateStore.getStateFromStore());
    const checkWith = ifToAddOrRemoveThisIntentWhenAllPass(
      deactivatedUpon,
      userIntents,
      '',
      '',
      loanUsersSpecificIntents,
      loanApp
    );
    return {
      shouldRun: !pathOr(false, ['remove'], checkWith),
    };
  }
  return { shouldRun: true }
};

